/**
 * ItemPoolPickerModal — modal variant of the pool picker: search + a list of
 * pools grouped by cycle/track, for flows where an inline combobox
 * (ItemPoolCombobox) doesn't fit (e.g. picking a pool as the first step of a
 * larger action). Follows the same modal chrome as CreateItemModal.tsx.
 */

import { useMemo, useState, useEffect } from 'react';
import { X, Search, ListChecks } from 'lucide-react';
import { useItemPoolsStore } from '../../../../stores/prioritization/itemPoolsStore';
import { useTrackStore } from '../../../../stores/prioritization/trackStore';
import { useDegapProcessStore } from '../../../../stores/prioritization/degapProcessStore';
import { ITEM_POOL_STATUS_LABELS, ITEM_POOL_STATUS_COLORS, type ItemPool } from '../../../../types/prioritization/lifecycle';

export interface ItemPoolPickerModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (poolId: string) => void;
  /** Scope the list to a specific cycle (optional). */
  cycleId?: string;
  /** Scope the list to a specific track (optional). */
  trackId?: string;
}

interface PoolGroup {
  key: string;
  label: string;
  pools: ItemPool[];
}

export function ItemPoolPickerModal({ open, onClose, onSelect, cycleId, trackId }: ItemPoolPickerModalProps) {
  const itemPools = useItemPoolsStore((s) => s.itemPools);
  const tracks = useTrackStore((s) => s.tracks);
  const cycles = useDegapProcessStore((s) => s.cycles);
  const [query, setQuery] = useState('');

  const trackNameById = useMemo(() => Object.fromEntries(tracks.map((t) => [t.id, t.name])), [tracks]);
  const cycleNameById = useMemo(() => Object.fromEntries(cycles.map((c) => [c.id, c.name])), [cycles]);

  const groups = useMemo((): PoolGroup[] => {
    const q = query.trim().toLowerCase();
    const scoped = itemPools.filter(
      (p) =>
        (cycleId ? p.cycleId === cycleId : true) &&
        (trackId ? p.trackId === trackId : true) &&
        (q ? p.name.toLowerCase().includes(q) : true)
    );

    const byGroupKey = new Map<string, PoolGroup>();
    for (const pool of scoped) {
      const groupKey = pool.cycleId ?? pool.trackId ?? '__ungrouped__';
      const groupLabel = pool.cycleId
        ? cycleNameById[pool.cycleId] ?? 'Cycle'
        : pool.trackId
          ? trackNameById[pool.trackId] ?? 'Track'
          : 'Ungrouped';
      const existing = byGroupKey.get(groupKey);
      if (existing) existing.pools.push(pool);
      else byGroupKey.set(groupKey, { key: groupKey, label: groupLabel, pools: [pool] });
    }

    return Array.from(byGroupKey.values())
      .map((g) => ({ ...g, pools: [...g.pools].sort((a, b) => a.name.localeCompare(b.name)) }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [itemPools, cycleId, trackId, query, cycleNameById, trackNameById]);

  useEffect(() => {
    if (open) setQuery('');
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  const totalCount = groups.reduce((sum, g) => sum + g.pools.length, 0);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-label="Select an item pool"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-[var(--bg-raised)] shadow-2xl shadow-gray-300/40 border border-[var(--rule)]/80 w-full max-w-lg max-h-[80vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-4 pb-3 border-b border-[var(--rule)]">
          <div className="flex items-center gap-2.5">
            <div className="flex items-center justify-center w-7 h-7 bg-[var(--slate-dim)]">
              <ListChecks className="h-3.5 w-3.5 text-[var(--text-ter)]" />
            </div>
            <h2 className="text-base font-semibold text-[var(--text-pri)]">Select a pool</h2>
          </div>
          <button onClick={onClose} className="p-1.5 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Search */}
        <div className="px-6 pt-3 pb-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-ter)]" />
            <input
              type="text"
              autoFocus
              placeholder="Search pools…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-indigo-100 focus:border-[var(--slate)] outline-none text-sm"
            />
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto px-3 pb-4">
          {totalCount === 0 ? (
            <div className="text-center py-10 text-[var(--text-ter)]">
              <ListChecks className="h-8 w-8 mx-auto mb-2 text-[var(--text-ter)]" />
              <p className="text-sm font-medium text-[var(--text-sec)]">No pools found</p>
            </div>
          ) : (
            <div className="space-y-4">
              {groups.map((group) => (
                <div key={group.key}>
                  <div className="px-2 mb-1 text-[10px] font-semibold text-[var(--text-ter)] uppercase tracking-widest">{group.label}</div>
                  <div className="space-y-1">
                    {group.pools.map((pool) => (
                      <button
                        key={pool.id}
                        onClick={() => {
                          onSelect(pool.id);
                          onClose();
                        }}
                        className="w-full flex items-center justify-between gap-2 px-3 py-2 border border-transparent hover:border-[var(--slate)] hover:bg-[var(--slate-dim)]/40 text-left transition-all"
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-[var(--text-pri)] truncate">{pool.name}</p>
                          <p className="text-xs text-[var(--text-ter)]">{pool.priorityIds.length} item{pool.priorityIds.length !== 1 ? 's' : ''}</p>
                        </div>
                        <span className={`flex-shrink-0 text-[11px] font-medium px-2 py-0.5 rounded-full ${ITEM_POOL_STATUS_COLORS[pool.status]}`}>
                          {ITEM_POOL_STATUS_LABELS[pool.status] ?? pool.status}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ItemPoolPickerModal;
